import { OrgEmailPreferences } from "@eip/shared/types";
import { queryKeys } from "../../utilities/queryKeys";
import { useMutationWithUI } from "../../utilities/useMutationWithUI";
import {
  updateNewMemberAcceptanceStatus,
  updateRegionSettings,
  updatePortalMode,
  reassignRegionService,
} from "./regionService";
import { updateOrgEmailSettings } from "./orgEmailSettingsService";

export function useUpdateNewMemberAcceptanceStatus() {
  return useMutationWithUI({
    mutationFn: (status: "active" | "inactive") => updateNewMemberAcceptanceStatus(status),
    invalidateKeys: [queryKeys.dashboards.admin()],
    successMessage: "Member acceptance status updated",
  });
}

export function useUpdateRegionSettings() {
  return useMutationWithUI({
    mutationFn: (data: {
      retainerInvoiceEnabled?: boolean;
      applicationIntake?: {
        isOpen: boolean;
        startDate: string | null;
        endDate: string | null;
        closedTitle: { eng: string; fr: string };
        closedMessage: { eng: string; fr: string };
      };
      productCapOverrides?: Record<string, { soft: number | null; hard: number | null }>;
      hourlyRateSoftCap?: number;
      intakeAllowlistEmails?: string[];
    }) => updateRegionSettings(data),
    // Editing caps changes what Service Providers see on createSOW2 (cross-role),
    // so cascade to every dashboard (Rule 2), bust the SP sow-caps query, and
    // refresh founder intake status (allowlist/intake edits change it).
    invalidateKeys: [queryKeys.dashboards.all, ["sowCaps"], queryKeys.intakeStatus()],
    successMessage: "Region settings updated",
  });
}

export function useUpdateOrgEmailSettings(successMessage: string, failureMessage: string) {
  return useMutationWithUI({
    mutationFn: (preferences: OrgEmailPreferences) => updateOrgEmailSettings(preferences),
    // Org-level email muting only — no query anywhere renders these emails,
    // so no dashboards.all cascade needed (Rule 2 exemption).
    invalidateKeys: [queryKeys.regionSettings.orgEmailSettings()],
    successMessage,
    failureMessage,
    // Toggle UX — no full-screen overlay per flip
    showLoadingOverlay: false,
  });
}

export function useUpdatePortalMode() {
  return useMutationWithUI({
    mutationFn: (args: { region: string; fullPortalEnabled: boolean }) =>
      updatePortalMode(args.region, args.fullPortalEnabled),
    invalidateKeys: [queryKeys.dashboards.admin()],
    successMessage: "Portal mode updated",
  });
}

export function useReassignRegion() {
  return useMutationWithUI({
    mutationFn: (args: { applicationId: string; targetRegion: string }) =>
      reassignRegionService(args.applicationId, args.targetRegion),
    invalidateKeys: [queryKeys.dashboards.admin(), queryKeys.members.all],
    successMessage: "Region reassigned",
  });
}
